import { useContext, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { AdminPanelSettings, Hub, List, Lock, Psychology } from '@mui/icons-material'
import { Alert, AlertTitle, Button, Collapse } from '@mui/material'
import { com } from '@eidu/form'
import { EntityTypesContext } from '../io/context/EntityTypes'
import SnackBarProvider from '../components/feedback/SnackBarProvider'
import DrawerScaffold, { DrawerItem, DrawerSection } from '../components/layout/DrawerScaffold'
import LoadingOverlay from '../components/feedback/LoadingOverlay'
import { iconForEntityTypeKind } from './entity/kindViewRegistry'
import { isAdminUserType } from '../util/entityType/isAdminUserType'
import useHasGlobalPermissions from './permission/useHasGlobalPermissions'
import {
  assetSidebarSections,
  documentSidebarSections,
  entitySidebarSections,
} from '../components/navigation/SidebarSection'
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import EntityType = com.eidu.sharedlib.entity.type.EntityType

const LandingPage = () => {
  const entityTypeRepository = useContext(EntityTypesContext)

  useEffect(() => {
    entityTypeRepository.getAll().then()
  }, [])

  const { hasPermission: hasGlobalPermission, error: fetchPermissionsError } = useHasGlobalPermissions(
    GlobalAction.ManageForms,
    GlobalAction.ManageEntityTypes,
    GlobalAction.ManagePermissionGrants,
    GlobalAction.ManageAuthentication
  )

  const entityTypes = entityTypeRepository.useSortedEntityTypes() ?? []

  const error = entityTypeRepository.useError()
  const loading = entityTypeRepository.useLoading()

  if (error) {
    return (
      <SnackBarProvider>
        <Collapse in={!!error}>
          <Alert
            variant="filled"
            severity="error"
            sx={{ margin: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={async () => {
                  await entityTypeRepository.clearError()
                  await entityTypeRepository.refreshAll()
                }}
              >
                Refresh
              </Button>
            }
          >
            <AlertTitle>Failed to load entities. Please try again.</AlertTitle>
            <div>{String(error)}</div>
          </Alert>
        </Collapse>
      </SnackBarProvider>
    )
  }

  if (fetchPermissionsError) {
    return (
      <SnackBarProvider>
        <Collapse in={!!fetchPermissionsError}>
          <Alert
            variant="filled"
            severity="error"
            sx={{ margin: 2 }}
            action={
              <Button color="inherit" size="small" onClick={async () => window.location.reload()}>
                Refresh
              </Button>
            }
          >
            <AlertTitle>Failed to load permissions. Please reload the page.</AlertTitle>
            <div>{String(fetchPermissionsError)}</div>
          </Alert>
        </Collapse>
      </SnackBarProvider>
    )
  }

  const createEntityTypeItem = (
    entityType: EntityType,
    icon = iconForEntityTypeKind(entityType.kind, <List />)
  ): DrawerItem => ({
    icon,
    text: entityType.name,
    key: `type-${entityType.id.toString()}`,
    path: `/entities/${entityType.id.toString()}`,
  })

  const canManageEntityTypes = hasGlobalPermission(GlobalAction.ManageEntityTypes)
  const canManagePermissionGrants = hasGlobalPermission(GlobalAction.ManagePermissionGrants)
  const canManageAdminUsers = hasGlobalPermission(GlobalAction.ManageAuthentication)

  const adminUsersEntityType = entityTypes.find(
    (type) => type.kind.equals(EntityTypeKind.User) && isAdminUserType(type.name)
  )

  const adminItems: DrawerItem[] = []
  if (canManageEntityTypes) {
    adminItems.push({
      icon: <Hub />,
      text: 'Types',
      key: 'types',
      path: `/types`,
    })
    adminItems.push({
      icon: <Psychology />,
      text: 'Semantics',
      key: 'semantics',
      path: `/semantics`,
    })
  }
  if (canManagePermissionGrants) {
    adminItems.push({
      icon: <Lock />,
      text: 'Access Control',
      key: 'access-control',
      path: `/access-control`,
    })
  }
  if (canManageAdminUsers && adminUsersEntityType) {
    adminItems.push({
      icon: <AdminPanelSettings />,
      text: 'Admin Users',
      key: `admin-user-${adminUsersEntityType.id.toString()}`,
      path: `/entities/${adminUsersEntityType.id.toString()}`,
    })
  }

  const adminEntityTypeId = adminUsersEntityType?.id.toString()
  const excludeAdminEntityType = adminEntityTypeId ? new Set([adminEntityTypeId]) : new Set<string>()

  const entityTypeItemsByKind = (kind: EntityTypeKind, exclusions: Set<string> = new Set()): DrawerItem[] =>
    entityTypes
      .filter((type) => type.kind.equals(kind) && !exclusions.has(type.id.toString()))
      .map((type) => createEntityTypeItem(type))

  const sections: DrawerSection[] = []

  if (adminItems.length) {
    sections.push({
      id: 'administration',
      title: 'Administration',
      items: adminItems,
      dividerBefore: false,
    })
  }

  const pushSection = (id: string, title: string, items: DrawerItem[], dividerBefore: boolean) => {
    if (!items.length) return
    sections.push({ id, title, items, dividerBefore })
  }

  const entityItems = entitySidebarSections.map((section) => ({
    ...section,
    items: entityTypeItemsByKind(
      section.kind,
      section.kind.equals(EntityTypeKind.User) ? excludeAdminEntityType : new Set()
    ),
  }))
  const visibleEntitySections = entityItems.filter(({ items }) => items.length > 0)

  visibleEntitySections.forEach(({ id, title, items }, index) => {
    pushSection(id, title, items, index === 0 && sections.length > 0)
  })

  const documentItems = documentSidebarSections.map((section) => ({
    ...section,
    items: entityTypeItemsByKind(section.kind),
  }))
  const visibleDocumentSections = documentItems.filter(({ items }) => items.length > 0)

  visibleDocumentSections.forEach(({ id, title, items }, index) => {
    pushSection(id, title, items, index === 0 && sections.length > 0)
  })

  const assetItems = assetSidebarSections.map((section) => ({
    ...section,
    items: entityTypeItemsByKind(section.kind),
  }))
  const visibleAssetSections = assetItems.filter(({ items }) => items.length > 0)

  visibleAssetSections.forEach(({ id, title, items }, index) => {
    pushSection(id, title, items, index === 0 && sections.length > 0)
  })

  return (
    <SnackBarProvider>
      <DrawerScaffold sections={sections}>
        <Outlet />
      </DrawerScaffold>
      <LoadingOverlay isOpen={loading} />
    </SnackBarProvider>
  )
}

export default LandingPage
